<template>
  <div class="d-flex">
    <template v-if="webhook.is_accepted">
      The message was accepted by the email delivery system.
    </template>
    <template v-else-if="webhook.is_delivered">
      The message was delivered to the <code class="mx-1">{{ webhook.recipient_domain }}</code> email server.
    </template>
    <div v-else-if="webhook.is_open"
         class="d-flex align-center">
      <div v-if="contact && contact.greeting">
        <div v-if="location">
          {{ contact?.greeting }} opened the email from <strong>{{ location }}</strong>
        </div>
        <div v-else>
          {{ contact?.greeting }} opened the email.
        </div>
      </div>
      <div v-else
           class="d-flex align-center">
        <div v-if="location">
          Opened the email from <strong>{{ location }}</strong>
        </div>
        <div v-else>
          Opened the email.
        </div>
      </div>
    </div>
    <template v-else-if="webhook.is_click">
      <template v-if="webhook.is_bot">
        <v-icon small
                color="success"
                class="mr-1">
          fas fa-fw fa-user-robot
        </v-icon>
        <div class="mr-1">
          The email security bot clicked
        </div>
        <div v-if="clickedPresentation"
             class="d-flex align-center">
          <a :href="clickedPresentation.open_url"
             target="_blank"
             class="font-weight-bold mr-1">
            {{ clickedPresentation?.name }}
          </a>
        </div>
        <div v-else
             class="d-flex align-center">
          <div>
            {{ truncatedURL }}
          </div>
        </div>
      </template>
      <template v-else>
        <div v-if="contact && contact.greeting"
             class="d-flex align-center">
          <div class="mr-1">
            {{ contact.greeting }} clicked
          </div>
          <a v-if="clickedPresentation"
             :href="clickedPresentation.open_url"
             target="_blank"
             class="font-weight-bold">
            {{ clickedPresentation.name }}
          </a>
          <div v-else>
            {{ truncatedURL }}
          </div>
          <div v-if="location"
               class="ml-1">
            from <strong>{{ location }}</strong>
          </div>
        </div>
        <div v-else
             class="d-flex align-center">
          <div class="mr-1">
            Clicked
          </div>
          <a v-if="clickedPresentation"
             :href="clickedPresentation.open_url"
             target="_blank"
             class="font-weight-bold">
            {{ clickedPresentation.name }}
          </a>
          <div v-else>
            {{ truncatedURL }}
          </div>
          <div v-if="location"
               class="ml-1">
            from <strong>{{ location }}</strong>
          </div>
        </div>
      </template>
    </template>
    <template v-else-if="webhook.is_failure_temporary">
      <div>
        The email temporarily failed. Code: ({{ webhook.delivery_status_code }})
      </div>
      <div class="CodeBlock">
        {{ deliveryStatusMessage }}
      </div>
    </template>
    <template v-else-if="webhook.is_failure_permanent">
      <div>
        The email permanently failed. Code: ({{ webhook.delivery_status_code }})
      </div>
      <div class="CodeBlock">
        {{ deliveryStatusMessage }}
      </div>
    </template>
    <template v-else-if="webhook.is_complaint">
      {{ webhook.recipient }} complained that this message was spam.
    </template>
    <template v-else-if="webhook.is_unsubscribe">
      {{ webhook.recipient }} unsubscribed.
    </template>
  </div>
</template>
<script>
import {isInteger, isObject, truncate} from 'lodash';
import DOMPurify from 'dompurify';

export default {
  props: {
    message: {
      type: Object,
      required: true,
    },
    webhook: {
      type: Object,
      required: true,
    }
  },
  computed: {
    contact() {
      return this.message.userContact;
    },
    link() { // MLD-7813
      if (isObject(this.webhook.sharedLink)) {
        return this.webhook.sharedLink;
      }
    },
    clickedPresentation() { // MLD-7813
      if (isObject(this.link?.presentation) && isInteger(this.link.presentation.id)) {
        return this.link.presentation;
      }
    },
    location() { // MLD-7836
      if (this.webhook.ipAddress && isObject(this.webhook.ipAddress.geolocation)) {
        const geolocation = this.webhook.ipAddress.geolocation;
        const parts = [];
        if (geolocation.city) {
          parts.push(geolocation.city);
        }
        if (geolocation.state) {
          parts.push(geolocation.state);
        }
        if (geolocation.country) {
          parts.push(geolocation.country);
        }
        if (parts.length > 0) {
          return DOMPurify.sanitize(parts.join(', '))
        }
      }
      return null;
    },
    truncatedURL() {
      const length = 70;
      if (this.webhook.clicked_url) {
        // MLD-7813
        return truncate(this.webhook.clicked_url, {
          length: length,
          omission: '... (Truncated)',
        });
      }
      return null;
    },
    eventData() {
      if (this.webhook.request
        && this.webhook.request.input
        && this.webhook.request.input['event-data']) {
        return this.webhook.request.input['event-data'];
      }
      return null;
    },
    deliveryStatus() {
      if (this.eventData && this.eventData['delivery-status']) {
        return this.webhook.request.input['event-data']['delivery-status'];
      }
      return null;
    },
    failureReason() {
      if (this.eventData) {
        return this.eventData.reason.toUpperCase();
      }
      return null;
    },
    deliveryStatusMessage() {
      if (this.deliveryStatus) {
        return this.deliveryStatus.message;
      }
      return null;
    }
  }
}
</script>
